import type { AgentDraftGoal } from "./agentDraft";
import { type AgentSubject, TemplateVariableType } from "./AgentRecord";

/** How an agent names one kind of record it runs on, wherever it names it. */
interface AgentSubjectSpec {
  /** What the editor calls the record. */
  label: string;
  /** The implicit variable a goal, or a goal-less agent, names the record by. */
  variableName: string;
  /** That variable as a placeholder, `{{variableName}}`. */
  placeholder: string;
  /** The type the implicit variable is declared with. */
  variableType: TemplateVariableType;
  /** The implicit variable's fixed description, which nobody edits. */
  variableDescription: string;
  /** The goal field that holds the record's id. */
  goalField: "dataset_id" | "session_id";
  /** The ``client_context`` list a goal-less agent names the record in. */
  clientContextKey: "dataset_ids" | "session_ids";
  /** The launch page's query parameter that carries the record's id. */
  queryParam: string;
}

/** Every kind of record an agent runs on, and how an agent names it. */
export const AGENT_SUBJECTS: Readonly<Record<AgentSubject, AgentSubjectSpec>> =
  {
    dataset: {
      label: "Dataset",
      variableName: "dataset.id",
      placeholder: "{{dataset.id}}",
      variableType: TemplateVariableType.DATASET_ID,
      variableDescription:
        "Dataset ID. Filled in from the dataset the agent is launched on.",
      goalField: "dataset_id",
      clientContextKey: "dataset_ids",
      queryParam: "dataset_id",
    },
    session: {
      label: "Session",
      variableName: "session.id",
      placeholder: "{{session.id}}",
      variableType: TemplateVariableType.SESSION_ID,
      variableDescription:
        "Session ID. Filled in from the session the agent is launched on.",
      goalField: "session_id",
      clientContextKey: "session_ids",
      queryParam: "session_id",
    },
  };

/** The keys of {@link AGENT_SUBJECTS}. */
export const AGENT_SUBJECT_KINDS = Object.keys(
  AGENT_SUBJECTS,
) as AgentSubject[];

/** The subject whose implicit variable is named `name`, or `undefined`. */
export function subjectOfVariableName(name: string): AgentSubject | undefined {
  return AGENT_SUBJECT_KINDS.find(
    (kind) => AGENT_SUBJECTS[kind].variableName === name,
  );
}

/**
 * The subject whose implicit variable is named `name`, when that is the
 * subject the agent runs on; otherwise `undefined`. Only the running subject's
 * variable is filled in at launch, so only it has a fixed type and
 * description. The other subject's name is an ordinary variable the author
 * describes, like any other they type.
 */
export function implicitSubjectOfVariableName(
  name: string,
  agentSubject: AgentSubject | "none" | null,
): AgentSubject | undefined {
  const subject = subjectOfVariableName(name);
  return subject !== undefined && subject === agentSubject
    ? subject
    : undefined;
}

/** The subject whose id a variable of `type` takes, or `undefined` for none. */
export function subjectOfVariableType(
  type: TemplateVariableType,
): AgentSubject | undefined {
  return AGENT_SUBJECT_KINDS.find(
    (kind) => AGENT_SUBJECTS[kind].variableType === type,
  );
}

/**
 * The record each known goal type works on, chosen when the agent is launched.
 * Keyed by every draft goal type, so a goal type added to `AgentDraftGoal`
 * fails the type check until it is mapped here.
 */
const GOAL_TYPE_SUBJECTS = {
  dataset_summary: "dataset",
  dataset_triage: "dataset",
  create_events: "dataset",
  session_summary: "session",
  session_triage: "session",
  session_create_events: "session",
} as const satisfies Record<AgentDraftGoal["goal_type"], AgentSubject>;

/** Every goal type this build knows, dataset goals first. */
export const GOAL_TYPES = Object.keys(
  GOAL_TYPE_SUBJECTS,
) as readonly AgentDraftGoal["goal_type"][];

const SUBJECTS_BY_GOAL_TYPE: ReadonlyMap<string, AgentSubject> = new Map(
  Object.entries(GOAL_TYPE_SUBJECTS),
);

/**
 * The record a goal of `goalType` works on, or `undefined` for a goal type
 * this build does not know.
 */
export function goalTypeSubject(
  goalType: AgentDraftGoal["goal_type"],
): AgentSubject;
export function goalTypeSubject(goalType: unknown): AgentSubject | undefined;
export function goalTypeSubject(goalType: unknown): AgentSubject | undefined {
  return typeof goalType === "string"
    ? SUBJECTS_BY_GOAL_TYPE.get(goalType)
    : undefined;
}
